import { Calendar, Clock, Repeat, Save, X } from "lucide-react";
import { useState } from "react";

interface FlowSchedulerProps {
  onSave: (schedule: {
    schedule_type: string;
    timezone: string;
    run_at_time: string;
    days_of_week?: number[];
    interval_days?: number;
    start_date?: string;
    input_prompt?: string;
  }) => void;
  onCancel: () => void;
  initialSchedule?: {
    schedule_type: string;
    timezone: string;
    run_at_time: string;
    days_of_week?: number[];
    interval_days?: number;
    start_date?: string;
    input_prompt?: string;
  };
  /** Placeholder hint for the input prompt field */
  defaultInputPrompt?: string;
  title?: string;
  subtitle?: string;
  showStartDate?: boolean;
  showInputPrompt?: boolean;
}

const WEEKDAYS = [
  { label: "Mon", value: 0 },
  { label: "Tue", value: 1 },
  { label: "Wed", value: 2 },
  { label: "Thu", value: 3 },
  { label: "Fri", value: 4 },
  { label: "Sat", value: 5 },
  { label: "Sun", value: 6 },
];

const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Dubai",
  "Australia/Sydney",
];

function getInputPromptPlaceholder(defaultInputPrompt: string | undefined): string {
  if (!defaultInputPrompt) {
    return "What should this flow process on each run? (URL, prompt, instructions...)";
  }
  const truncated = defaultInputPrompt.slice(0, 120);
  const suffix = defaultInputPrompt.length > 120 ? "..." : "";
  return `Default: ${truncated}${suffix}`;
}

export function FlowScheduler({
  onSave,
  onCancel,
  initialSchedule,
  defaultInputPrompt,
  title = "Schedule Flow",
  subtitle = "Set up automated execution",
  showStartDate = true,
  showInputPrompt = true,
}: Readonly<FlowSchedulerProps>) {
  const [scheduleType, setScheduleType] = useState(initialSchedule?.schedule_type || "daily");
  const [timezone, setTimezone] = useState(initialSchedule?.timezone || "UTC");
  const [runTime, setRunTime] = useState(initialSchedule?.run_at_time || "09:00");
  const [selectedDays, setSelectedDays] = useState<number[]>(
    initialSchedule?.days_of_week || [0, 1, 2, 3, 4], // Default to weekdays
  );
  const [intervalDays, setIntervalDays] = useState(initialSchedule?.interval_days || 1);
  const [startDate, setStartDate] = useState(initialSchedule?.start_date || new Date().toISOString().split("T")[0]);
  const [inputPrompt, setInputPrompt] = useState(initialSchedule?.input_prompt || "");

  const timezoneOptions = TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES];

  const handleToggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b),
    );
  };

  const handleSave = () => {
    const schedule: {
      schedule_type: string;
      timezone: string;
      run_at_time: string;
      days_of_week?: number[];
      interval_days?: number;
      start_date?: string;
      input_prompt?: string;
    } = {
      schedule_type: scheduleType,
      timezone,
      run_at_time: runTime,
    };

    if (scheduleType === "weekly") {
      schedule.days_of_week = selectedDays;
    } else if (scheduleType === "custom") {
      schedule.interval_days = intervalDays;
    }

    if (showStartDate) {
      schedule.start_date = startDate;
    }

    if (showInputPrompt && inputPrompt.trim()) {
      schedule.input_prompt = inputPrompt.trim();
    }

    onSave(schedule);
  };

  const getSchedulePreview = () => {
    const time = new Date(`2000-01-01T${runTime}`).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });

    if (scheduleType === "daily") {
      return `Every day at ${time} (${timezone})`;
    } else if (scheduleType === "weekly") {
      const days = selectedDays.map((d) => WEEKDAYS[d].label).join(", ");
      return `Every ${days} at ${time} (${timezone})`;
    } else {
      const plural = intervalDays === 1 ? "day" : "days";
      return `Every ${intervalDays} ${plural} at ${time} (${timezone})`;
    }
  };

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center">
            <Clock className="h-5 w-5 text-brand-primary" />
          </div>
          <div>
            <h3 className="font-medium text-content-primary">{title}</h3>
            <p className="text-sm text-content-tertiary">{subtitle}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close scheduler"
          className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-6">
        {/* Schedule Type */}
        <div>
          <span className="block text-sm font-medium text-content-secondary mb-3">Schedule Type</span>
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setScheduleType("daily")}
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                scheduleType === "daily"
                  ? "border-brand-primary bg-brand-primary-subtle"
                  : "border-border-secondary bg-surface-secondary hover:border-border-hover"
              }`}
            >
              <Calendar className="h-5 w-5 text-brand-primary" />
              <span className="text-sm font-medium text-content-primary">Daily</span>
            </button>
            <button
              type="button"
              onClick={() => setScheduleType("weekly")}
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                scheduleType === "weekly"
                  ? "border-brand-primary bg-brand-primary-subtle"
                  : "border-border-secondary bg-surface-secondary hover:border-border-hover"
              }`}
            >
              <Clock className="h-5 w-5 text-brand-primary" />
              <span className="text-sm font-medium text-content-primary">Weekly</span>
            </button>
            <button
              type="button"
              onClick={() => setScheduleType("custom")}
              className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                scheduleType === "custom"
                  ? "border-brand-primary bg-brand-primary-subtle"
                  : "border-border-secondary bg-surface-secondary hover:border-border-hover"
              }`}
            >
              <Repeat className="h-5 w-5 text-brand-primary" />
              <span className="text-sm font-medium text-content-primary">Custom</span>
            </button>
          </div>
        </div>

        {/* Weekly: Day Selection */}
        {scheduleType === "weekly" && (
          <div>
            <span className="block text-sm font-medium text-content-secondary mb-3">Select Days</span>
            <div className="flex gap-2">
              {WEEKDAYS.map((day) => (
                <button
                  type="button"
                  key={day.value}
                  onClick={() => handleToggleDay(day.value)}
                  className={`flex-1 px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                    selectedDays.includes(day.value)
                      ? "bg-brand-primary text-white"
                      : "bg-surface-secondary text-content-tertiary hover:bg-interactive-active"
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Custom: Interval */}
        {scheduleType === "custom" && (
          <div>
            <label htmlFor="flow-interval-days" className="block text-sm font-medium text-content-secondary mb-2">
              Run Every X Days
            </label>
            <input
              id="flow-interval-days"
              type="number"
              min="1"
              max="365"
              value={intervalDays}
              onChange={(e) => setIntervalDays(Number.parseInt(e.target.value, 10) || 1)}
              className="w-full px-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>
        )}

        {/* Run Time */}
        <div>
          <label htmlFor="flow-run-time" className="block text-sm font-medium text-content-secondary mb-2">
            Run Time
          </label>
          <input
            id="flow-run-time"
            type="time"
            value={runTime}
            onChange={(e) => setRunTime(e.target.value)}
            className="w-full px-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
          />
        </div>

        {/* Timezone */}
        <div>
          <label htmlFor="flow-timezone" className="block text-sm font-medium text-content-secondary mb-2">
            Timezone
          </label>
          <select
            id="flow-timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="w-full px-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
          >
            {timezoneOptions.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>

        {/* Start Date */}
        {showStartDate && (
          <div>
            <label htmlFor="flow-start-date" className="block text-sm font-medium text-content-secondary mb-2">
              Start Date
            </label>
            <input
              id="flow-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="w-full px-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>
        )}

        {/* Input Prompt */}
        {showInputPrompt && (
          <div>
            <label htmlFor="flow-input-prompt" className="block text-sm font-medium text-content-secondary mb-2">
              Input Prompt / URL
            </label>
            <textarea
              id="flow-input-prompt"
              rows={3}
              value={inputPrompt}
              onChange={(e) => setInputPrompt(e.target.value)}
              placeholder={getInputPromptPlaceholder(defaultInputPrompt)}
              className="w-full px-4 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none"
            />
            <p className="text-xs text-content-quaternary mt-1">
              {defaultInputPrompt
                ? "Leave blank to use the flow's default input prompt."
                : "This is sent to the first step when the schedule fires."}
            </p>
          </div>
        )}

        {/* Preview */}
        <div className="bg-surface-secondary/50 border border-border-secondary rounded-lg p-4">
          <div className="text-xs font-medium text-content-tertiary mb-1">SCHEDULE PREVIEW</div>
          <div className="text-sm text-content-primary">{getSchedulePreview()}</div>
          {showStartDate && <div className="text-xs text-content-quaternary mt-1">Starting {startDate}</div>}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={scheduleType === "weekly" && selectedDays.length === 0}
            className="flex items-center gap-2 px-6 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            Save Schedule
          </button>
        </div>
      </div>
    </div>
  );
}
